import { defineNuxtPlugin } from '#app';
import { ref } from 'vue';

import { useVersionInfo } from '~/composables/useVersionInfo';

/**
 * Version Inspector Plugin
 *
 * Exposes version information globally for debugging purposes.
 *
 * Usage:
 * - Console: `window.showDebug()` - Opens the interaction debug panel
 * - Console: `window.hideDebug()` - Closes the interaction debug panel
 * - Console: `window.showVersion()` - Opens the version panel
 * - Console: `window.hideVersion()` - Closes the version panel
 * - Console: `window.logVersion()` - Logs version info to console
 * - Console: `window.getVersion()` - Returns version info object
 *
 * Triggers:
 * - Keyboard: Press Alt + Shift + D three times quickly to toggle interaction debug
 * - Keyboard: Press Alt + Shift + V three times quickly to toggle version info
 * - PostMessage: Send `{ type: 'showDebug' }` / `hideDebug` / `toggleDebug`
 * - PostMessage: Send `{ type: 'showVersion' }` / `hideVersion` / `toggleVersion`
 *
 * For iOS/Android native apps, trigger via postMessage from native code.
 */
export default defineNuxtPlugin(() => {
    // Only run on client side
    if (import.meta.server) return;

    const { versionInfo, logVersionInfo } = useVersionInfo();
    const interactionDebugVisible = ref(false);
    const versionPanelVisible = ref(false);
    const showDebug = () => (interactionDebugVisible.value = true);
    const hideDebug = () => (interactionDebugVisible.value = false);
    const toggleDebug = () => (interactionDebugVisible.value = !interactionDebugVisible.value);
    const showVersion = () => (versionPanelVisible.value = true);
    const hideVersion = () => (versionPanelVisible.value = false);
    const toggleVersion = () => (versionPanelVisible.value = !versionPanelVisible.value);

    // Track each shortcut independently so one sequence cannot affect the other.
    const keyPressCounts = { KeyD: 0, KeyV: 0 };
    const keyPressTimers: Partial<
        Record<keyof typeof keyPressCounts, ReturnType<typeof setTimeout>>
    > = {};
    const KEY_COMBO_TIMEOUT = 500; // ms between key presses

    const handleKeyPress = (e: KeyboardEvent) => {
        // Ignore if user is typing in an input
        if (
            e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLTextAreaElement ||
            (e.target instanceof HTMLElement && e.target.isContentEditable)
        ) {
            return;
        }

        // Require Alt + Shift + D or V to be pressed 3 times
        // Use e.code instead of e.key to avoid issues with Alt producing special characters
        if ((e.code === 'KeyD' || e.code === 'KeyV') && e.altKey && e.shiftKey) {
            const code = e.code;
            keyPressCounts[code]++;

            if (keyPressTimers[code]) {
                clearTimeout(keyPressTimers[code]);
            }

            if (keyPressCounts[code] >= 3) {
                keyPressCounts[code] = 0;
                if (code === 'KeyD') {
                    toggleDebug();
                } else {
                    toggleVersion();
                }
            } else {
                keyPressTimers[code] = setTimeout(() => {
                    keyPressCounts[code] = 0;
                }, KEY_COMBO_TIMEOUT);
            }
        }
    };

    // Handle postMessage from native apps
    const handleMessage = (e: MessageEvent) => {
        // Try to parse if it's a string
        let data = e.data;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                return;
            }
        }

        if (!data || typeof data !== 'object') return;

        switch (data.type) {
            case 'showDebug':
                showDebug();
                break;
            case 'hideDebug':
                hideDebug();
                break;
            case 'toggleDebug':
                toggleDebug();
                break;
            case 'showVersion':
                showVersion();
                break;
            case 'hideVersion':
                hideVersion();
                break;
            case 'toggleVersion':
                toggleVersion();
                break;
            case 'getVersion':
                // Post back the version info
                window.parent?.postMessage(
                    {
                        type: 'versionInfo',
                        data: versionInfo.value,
                    },
                    '*'
                );
                break;
        }
    };

    // Set up event listeners
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('message', handleMessage);

    // Expose global functions
    window.logVersion = () => {
        return logVersionInfo();
    };

    window.getVersion = () => {
        return versionInfo.value;
    };

    window.showDebug = showDebug;
    window.hideDebug = hideDebug;
    window.toggleDebug = toggleDebug;
    window.showVersion = showVersion;
    window.hideVersion = hideVersion;
    window.toggleVersion = toggleVersion;

    return {
        provide: {
            versionInfo,
            interactionDebugVisible,
            versionPanelVisible,
        },
    };
});

// Type declarations for global window functions
declare global {
    interface Window {
        logVersion: () => ReturnType<typeof useVersionInfo>['versionInfo']['value'];
        getVersion: () => ReturnType<typeof useVersionInfo>['versionInfo']['value'];
        showDebug: () => void;
        hideDebug: () => void;
        toggleDebug: () => void;
        showVersion: () => void;
        hideVersion: () => void;
        toggleVersion: () => void;
    }
}
